import useRoutes from '@/hooks/useRoutes';
import { useThemeColors } from '@/hooks/useThemeColors';
import { Course } from '@/types';
import { Link } from '@inertiajs/react';
import { BookOpen, Tag, User } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface CourseHeaderProps {
    course: Course;
    lastClassViewedId?: number;
    firstClassId?: number;
}

const CourseHeader: React.FC<CourseHeaderProps> = ({
    course,
    lastClassViewedId,
    firstClassId,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { route } = useRoutes();

    // Determine which class to link to and what button text to show
    const hasStartedCourse = !!lastClassViewedId;
    const targetClassId = lastClassViewedId || firstClassId;

    return (
        <div className="mb-8 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col xl:flex-row xl:items-stretch">
                {/* Course Image */}
                <div className="w-full flex-shrink-0 overflow-hidden xl:w-1/2 xl:self-stretch">
                    <div
                        className="h-full w-full"
                        style={{ aspectRatio: '16/9' }}
                    >
                        {course.image_url ? (
                            <img
                                src={course.image_url}
                                alt={course.title}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <div
                                className={`h-full w-full ${colors.primaryBg} flex items-center justify-center`}
                            >
                                <BookOpen className="h-12 w-12 text-white" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Course Info */}
                <div className="flex-1 p-6 xl:p-8">
                    <div
                        className={`mb-4 inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${colors.accent} ${colors.accentText}`}
                    >
                        <Tag className="mr-2 h-4 w-4" />
                        {course.category.name}
                    </div>

                    <h1 className="mb-3 text-2xl leading-tight font-bold text-gray-900 xl:text-3xl">
                        {course.title}
                    </h1>

                    {/* {course.description && (
                        <div
                            className="mb-6 text-base leading-relaxed text-gray-600"
                            dangerouslySetInnerHTML={{
                                __html: cleanWordPressHtml(course.description),
                            }}
                        />
                    )} */}

                    {/* Course Meta */}
                    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {course.professor?.full_name && (
                            <div className="flex items-center space-x-3">
                                <div
                                    className={`p-2 ${colors.accent} rounded-lg`}
                                >
                                    <User
                                        className={`h-4 w-4 text-${colors.primary}`}
                                    />
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">
                                        {t('home.professor')}
                                    </div>
                                    <div className="font-medium text-gray-900">
                                        {course.professor?.full_name}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center space-x-3">
                            <div className={`p-2 ${colors.accent} rounded-lg`}>
                                <BookOpen
                                    className={`h-4 w-4 text-${colors.primary}`}
                                />
                            </div>
                            <div className="text-sm text-gray-500">
                                <span>{t('home.sections')}</span>
                                <span className="ml-2 font-medium text-gray-900">
                                    {course.sections.length}
                                </span>
                            </div>
                        </div>
                    </div>

                    {targetClassId && (
                        <Link
                            className="btn-gradient rounded-lg px-6 py-3 font-semibold"
                            href={route('class-detail', {
                                slug: course.slug,
                                class_id: targetClassId,
                            }).toString()}
                        >
                            {hasStartedCourse
                                ? t('course.continueWhereYouLeftOff')
                                : t('course.startCourse')}
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CourseHeader;
